import React, { useState } from 'react';

import Link from 'next/link';

import Popover from '@repo/editor/src/components/Popover/Popover';
import {
  PopoverAlign,
  PopoverPlacement,
} from '@repo/editor/src/components/Popover/types';
import { IconButton } from '@repo/ui/components/Buttons';
import ArrowUpIcon from '@repo/ui/components/Icon/ArrowUpIcon';
import FacebookCircleIcon from '@repo/ui/components/Icon/FacebookCircleIcon';
import InstagramCircleIcon from '@repo/ui/components/Icon/InstagramCircleIcon';
import PinterestIcon from '@repo/ui/components/Icon/PinterestIcon';
import TwitterCircleIcon from '@repo/ui/components/Icon/TwitterCircleIcon';
import YoutubeCircleIcon from '@repo/ui/components/Icon/YoutubeCircleIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { themeStyle } from '@repo/ui/theming';

import Logo from 'components/Logo/Logo';
import { Container } from 'components/utilities/pageStyles';

import { options } from './config';
import {
  Label,
  RowWrapper,
  FooterWrapper,
  Separator,
  OptionWrapper,
  MoreWrapper,
  Wrapper,
  PopoverChildren,
  OptionGroup,
  ArrowWrapper,
} from './styles';
import { defaultTheme } from './theme';

const SmallFooter = (props) => {
  const currentYear = new Date().getFullYear();

  const theme = { ...defaultTheme, ...props.theme };

  const iconButtonTheme = {
    color: theme.color,
    activeColor: theme.darkColor,
  };

  const [isMoreActive, setIsMoreActive] = useState(false);

  const moreContent = (
    <MoreWrapper>
      <Link legacyBehavior passHref href="/about">
        <OptionWrapper height="31px" justifyContent="center" minWidth="74px">
          <Label theme={theme}>About</Label>
        </OptionWrapper>
      </Link>
      <Link legacyBehavior passHref href="/careers">
        <OptionWrapper
          height="31px"
          justifyContent="center"
          minWidth="74px"
          target="_blank"
        >
          <Label theme={theme}>Careers</Label>
        </OptionWrapper>
      </Link>
      <Link legacyBehavior passHref href="/pricing">
        <OptionWrapper height="31px" justifyContent="center" minWidth="74px">
          <Label theme={theme}>Pricing</Label>
        </OptionWrapper>
      </Link>
    </MoreWrapper>
  );

  return (
    <FooterWrapper data-testid="footer" theme={theme}>
      <Container>
        <Wrapper>
          <RowWrapper>
            <Link legacyBehavior passHref href="/">
              <OptionWrapper>
                <Logo
                  small
                  dataTestId="footer--kittl-logo"
                  theme={{
                    logoColor: themeStyle.brandMain,
                    textColor: themeStyle.inkBase,
                  }}
                />
              </OptionWrapper>
            </Link>
            <Spacer width="16px" />
            <Popover
              align={PopoverAlign.Center}
              content={moreContent}
              placement={PopoverPlacement.Top}
              showHeader={false}
              theme={{
                padding: '10px',
              }}
              width="100px"
              onClose={() => setIsMoreActive(false)}
            >
              <PopoverChildren theme={theme}>
                <RowWrapper
                  gap="5px"
                  onClick={() => setIsMoreActive((active) => !active)}
                >
                  <Label
                    cursor="pointer"
                    isActive={isMoreActive}
                    theme={{ ...theme, color: themeStyle.varInkMedium }}
                  >
                    More
                  </Label>
                  <ArrowWrapper isActive={isMoreActive}>
                    <ArrowUpIcon
                      height="4px"
                      isActive={isMoreActive}
                      theme={{
                        color: theme.color,
                        activeColor: theme.darkColor,
                      }}
                    />
                  </ArrowWrapper>
                </RowWrapper>
              </PopoverChildren>
            </Popover>
            {options.map((optionList, index) => (
              <OptionGroup key={index}>
                <Separator key="separator" theme={theme} />
                {optionList.map((option, idx) => (
                  <Link
                    key={`opt-${idx}`}
                    legacyBehavior
                    passHref
                    href={option.link}
                  >
                    <OptionWrapper target={option.newTab ? '_blank' : null}>
                      <Label theme={theme}>{option.label}</Label>
                    </OptionWrapper>
                  </Link>
                ))}
              </OptionGroup>
            ))}
          </RowWrapper>
          <RowWrapper>
            <Label doNotHover theme={theme}>
              &copy; Kittl {currentYear}
            </Label>
            <Separator theme={theme} />
            <Label doNotHover theme={theme}>
              Follow us:
            </Label>
            <Spacer width="12px" />
            <Link
              legacyBehavior
              passHref
              href="https://www.facebook.com/kittldesign"
            >
              <OptionWrapper rel="noreferrer" target="_blank">
                <IconButton
                  height="16px"
                  icon={FacebookCircleIcon}
                  theme={iconButtonTheme}
                />
              </OptionWrapper>
            </Link>
            <Link
              legacyBehavior
              passHref
              href="https://www.youtube.com/Kittldesign"
            >
              <OptionWrapper rel="noreferrer" target="_blank">
                <IconButton
                  height="16px"
                  icon={YoutubeCircleIcon}
                  theme={iconButtonTheme}
                />
              </OptionWrapper>
            </Link>
            <Link
              legacyBehavior
              passHref
              href="https://www.instagram.com/kittldesign/"
            >
              <OptionWrapper rel="noreferrer" target="_blank">
                <IconButton
                  height="16px"
                  icon={InstagramCircleIcon}
                  theme={iconButtonTheme}
                />
              </OptionWrapper>
            </Link>
            <Link
              legacyBehavior
              passHref
              href="https://www.pinterest.com/kittldesign"
            >
              <OptionWrapper rel="noreferrer" target="_blank">
                <IconButton
                  height="18px"
                  icon={PinterestIcon}
                  theme={iconButtonTheme}
                />
              </OptionWrapper>
            </Link>
            <Link
              legacyBehavior
              passHref
              href="https://twitter.com/kittldesign"
            >
              <OptionWrapper rel="noreferrer" target="_blank">
                <IconButton
                  height="18px"
                  icon={TwitterCircleIcon}
                  theme={iconButtonTheme}
                />
              </OptionWrapper>
            </Link>
          </RowWrapper>
        </Wrapper>
      </Container>
    </FooterWrapper>
  );
};

export default SmallFooter;
